import React, { useState, useEffect } from 'react';
import {
  Zap,
  Moon,
  Sun,
  Layout,
  Code2,
  GitBranch,
  Terminal,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Cpu,
  AlertTriangle,
  BrainCircuit,
  Gamepad2,
  Layers,
  FileCode,
  Globe,
  Mail,
  Workflow,
  Bot,
  Badge,
  MessageSquareCode,
  ShieldCheck,
  CpuIcon,
  FileText, FileSpreadsheet, HardDrive, Video, CreditCard, Clock,
  Loader2,
  Monitor,
  Command,
  Server
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient'; // Make sure path is correct

// --- Utility ---
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// --- Analytics Source Mapping ---
const SOURCE_MAP = {
'rss': 'Reddit - r/saas',
  'rgd': 'Reddit - r/gamedev',
  'rih': 'Reddit - r/indiehackers',
	  'rbp': 'Reddit - r/buildinpublic',
	  'rpd': 'Reddit - r/productivity',
'rpd': 'Reddit - General Programmers', //app most likly
  
	'rsp': 'Reddit - r/sideproject',
  'freelance': 'Freelance Subreddits',
	'rfd': 'Reddit - r/FigmaDesign',

	'rsd': 'Reddit - r/SoloDevelopment',
	'rud': 'Reddit - r/Unity3D',
	'rue': 'Reddit - r/UnrealEngine',

  'twitter': 'Twitter',
	'gaming': 'Gaming',
	'gp': 'General Developers',
  'linkedin': 'LinkedIn',
  'default': 'Organic / Direct'
};

// --- Data ---
const ROLE_DATA = [
  {
    label: "App Developer",
    article: "an",
    project: "SaaS Dashboard V2",
    primaryThrust: "You abandoned the `AuthGuard.ts` file at 2:00 AM with broken JWT validation logic:",
    tasks: [
      "Complete the token refresh cycle before the session times out.",
      "The middleware is currently blocking valid requests—fix line 42.",
      "Sync the new environment variables to your local `.env` file."
    ],
    risk: "Critical: You hardcoded a `SECRET_KEY` in the commit history 10 minutes ago.",
    inputs: [
      { icon: Code2, label: "3 Files Changed", color: "text-blue-600 bg-blue-50 dark:bg-blue-500/10 dark:text-blue-400" },
      { icon: GitBranch, label: "PR #402 Pending", color: "text-zinc-600 bg-zinc-100 dark:bg-zinc-800 dark:text-white" },
      { icon: Terminal, label: "Build Passing", color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-400" }
    ]
  },
  {
    label: "Freelancer",
    article: "a",
    project: "Client: Starline Inc.",
    primaryThrust: "The client just emailed asking for a 'quick tweak' to the video player, but your contract is fixed-bid:",
    tasks: [
      "Flag the request as 'Out of Scope' using the saved template.",
      "Reply with the hourly rate estimation for the video feature.",
      "Do not touch the code until the addendum is signed."
    ],
    risk: "Scope Creep: This 'tweak' requires a database migration you aren't charging for.",
    inputs: [
      { icon: Globe, label: "Staging Updated", color: "text-indigo-600 bg-indigo-50 dark:bg-indigo-500/10 dark:text-indigo-400" },
      { icon: FileCode, label: "Invoice Generated", color: "text-zinc-600 bg-zinc-100 dark:bg-zinc-800 dark:text-white" },
      { icon: Mail, label: "Email Scanned", color: "text-amber-600 bg-amber-50 dark:bg-amber-500/10 dark:text-amber-400" }
    ]
  },
  {
    label: "Graphic Designer",
    article: "a",
    project: "Spring Promo: Social Assets",
    primaryThrust: "You just saved `Hero_Composite.psd` with a new canvas size, which shifted the anchor points in After Effects:",
    tasks: [
      "Re-align the 'Logo Reveal' keyframes to match the new layer bounds.",
      "The 'Glow' effect is clipping—switch project color depth to 32bpc.",
      "Purge the Disk Cache; the RAM preview is showing old frames."
    ],
    risk: "Export Warning: You are rendering 60fps footage into a 24fps sequence, ghosting will occur.",
    inputs: [
      { icon: Layout, label: "Figma Updated 2h ago", color: "text-purple-600 bg-purple-50 dark:bg-purple-500/10 dark:text-purple-400" },
      { icon: Layers, label: "24 Assets Exported", color: "text-pink-600 bg-pink-50 dark:bg-pink-500/10 dark:text-pink-400" },
      { icon: CheckCircle2, label: "Client Approved", color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-400" }
    ]
  },
  {
    label: "Game Developer",
    article: "a",
    project: "Cyberpunk RPG",
    primaryThrust: "The new particle system you added to 'Sector 4' is tanking the frame rate on the Steam Deck build:",
    tasks: [
      "Reduce the particle count or switch to GPU instancing.",
      "The texture streaming pool is overflowing—compress the skybox.",
      "Check the physics collider on the new NPC; it's too complex."
    ],
    risk: "Performance: Frame time spiked to 32ms (Target: 16ms) in the last playtest.",
    inputs: [
      { icon: Gamepad2, label: "Unity Editor Active", color: "text-zinc-800 bg-zinc-100 dark:bg-white dark:text-zinc-200" },
      { icon: Code2, label: "C# Scripts Changed", color: "text-blue-600 bg-blue-50 dark:bg-blue-500/10 dark:text-blue-400" },
      { icon: Cpu, label: "Profiler: High Load", color: "text-red-600 bg-red-50 dark:bg-red-500/10 dark:text-red-400" }
    ]
  },
  {
    label: "Professional",
    article: "a",
    project: "Q3 Marketing Campaign",
    primaryThrust: "You have a Stakeholder Review in 45 minutes, but the Budget Spreadsheet doesn't match the Vendor Quotes:",
    tasks: [
      "Reconcile the $4k variance in the 'Social Ads' column.",
      "Pull the final engagement metrics from the Notion report.",
      "Export the deck to PDF—the video file on slide 4 is broken."
    ],
    risk: "Compliance: The 'Influencer' contract expired yesterday, do not launch the ads yet.",
    inputs: [
      { icon: Layout, label: "Notion Updated", color: "text-zinc-800 bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-200" },
      { icon: Mail, label: "5 Emails Flagged", color: "text-amber-600 bg-amber-50 dark:bg-amber-500/10 dark:text-amber-400" },
      { icon: Layers, label: "Slide Deck Ready", color: "text-orange-600 bg-orange-50 dark:bg-orange-500/10 dark:text-orange-400" }
    ]
  }
];

// --- Sub-Components ---

const FeatureCard = ({ icon: Icon, title, desc, isDarkMode }: any) => (
  <div className={cn(
    "p-8 rounded-2xl border transition-all duration-300 h-full flex flex-col shadow-sm",
    isDarkMode ? "bg-zinc-900 border-zinc-800 text-zinc-100" : "bg-white border-zinc-200 text-zinc-900"
  )}>
    <div className={cn(
      "w-12 h-12 rounded-xl flex items-center justify-center mb-6",
      isDarkMode ? "bg-purple-500/10 text-purple-400" : "bg-purple-50 text-purple-600"
    )}>
      <Icon size={24} />
    </div>
    <h3 className="text-xl font-bold mb-3">{title}</h3>
    <p className={cn("text-sm leading-relaxed", isDarkMode ? "text-zinc-400" : "text-zinc-600")}>{desc}</p>
  </div>
);

const GraveyardCard = ({ icon: Icon, title, quote, sub, isDarkMode }: any) => (
  <div className={cn(
    "relative p-8 border rounded-2xl overflow-hidden group transition-all h-full flex flex-col justify-between shadow-sm",
    isDarkMode ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200"
  )}>
    <div className={cn(
      "absolute -right-4 -bottom-4 transition-colors transform rotate-12 pointer-events-none",
      isDarkMode ? "text-zinc-800" : "text-zinc-100"
    )}>
       <Icon size={140} />
    </div>
    <div className="relative z-10">
      <h4 className={cn("text-xl font-bold mb-3", isDarkMode ? "text-white" : "text-zinc-900")}>{title}</h4>
      <p className={cn("text-sm font-medium italic mb-2", isDarkMode ? "text-zinc-400" : "text-zinc-600")}>"{quote}"</p>
    </div>
    <p className={cn("text-xs mt-6 relative z-10 border-t pt-4", isDarkMode ? "text-zinc-500 border-zinc-800" : "text-zinc-400 border-zinc-100")}>{sub}</p>
  </div>
);

// --- Early Access Component ---
const EarlyAccess = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({ name: '', email: '', os: 'Windows' });
  const [status, setStatus] = useState('idle');

  // Logic to grab source tag
  const sourceTag = searchParams.get('s') || 'default';
  const analyticsSource = SOURCE_MAP[sourceTag] || SOURCE_MAP['default'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    // Simulate API delay for demo purposes
    // Replace this with your actual Supabase Insert
    
    const { error } = await supabase
      .from('waitlist')
      .insert({
        name: formData.name,
        email: formData.email,
        work_os: formData.os,
        source: analyticsSource,
        created_at: new Date().toISOString()
      });
    
    
    // Simulating Success
    setTimeout(() => {
        console.log("Submitted:", { ...formData, source: analyticsSource });
        setStatus('success');
    }, 1500);
  };

  if (status === 'success') {
    return (
      <section id="early-access" className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-xl mx-auto text-center space-y-6 p-12 rounded-3xl border border-emerald-500/20 bg-emerald-500/5">
          <div className="w-16 h-16 bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} />
          </div>
          <h3 className="text-3xl font-black text-emerald-500">You're on the list!</h3>
          <p className={isDarkMode ? "text-zinc-400" : "text-zinc-600"}>
            We've reserved your spot for the <strong>{formData.os}</strong> beta. 
            Keep an eye on your inbox, we're onboarding users in small batches to ensure stability.
          </p>
          <button onClick={() => setStatus('idle')} className="text-sm font-bold underline opacity-50 hover:opacity-100 transition-opacity">
            Register another teammate
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="early-access" className={cn("py-24 px-6 border-b", isDarkMode ? "bg-zinc-950 border-zinc-900" : "bg-zinc-50 border-zinc-200")}>
      <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
           <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/20">Early Access</Badge>
           <h2 className="text-4xl md:text-5xl font-black tracking-tight leading-[0.95]">
             Ship better <br/> with <span className="text-purple-500">Thrust.</span>
           </h2>
           <p className={cn("text-lg leading-relaxed", isDarkMode ? "text-zinc-400" : "text-zinc-600")}>
             We are currently rolling out the Windows Native Beta.
             <br/><br/>
             Mac and Linux support is on the roadmap, but for now, we are optimizing for the platform builders actually use.
           </p>
           
           <div className="flex gap-4 pt-4 opacity-50">
              <Monitor size={20} className="text-purple-500" />
              <Command size={20} />
              <Server size={20} />
           </div>
        </div>

        <div className={cn(
          "p-8 rounded-3xl border shadow-2xl relative overflow-hidden",
          isDarkMode ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200"
        )}>
           {/* Form */}
           <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest opacity-50">Full Name</label>
                <input 
                  required
                  type="text" 
                  placeholder="John Doe"
                  className={cn(
                    "w-full px-4 py-3 rounded-xl border bg-transparent font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all",
                    isDarkMode ? "border-zinc-700 placeholder-zinc-700" : "border-zinc-200 placeholder-zinc-300"
                  )}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest opacity-50">Email Address</label>
                <input 
                  required
                  type="email" 
                  placeholder="john@studio.com"
                  className={cn(
                    "w-full px-4 py-3 rounded-xl border bg-transparent font-medium focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all",
                    isDarkMode ? "border-zinc-700 placeholder-zinc-700" : "border-zinc-200 placeholder-zinc-300"
                  )}
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest opacity-50">Primary Work OS</label>
                <div className="grid grid-cols-3 gap-2">
                   {['Windows', 'Mac', 'Linux'].map((os) => (
                      <button
                        key={os}
                        type="button"
                        onClick={() => setFormData({...formData, os})}
                        className={cn(
                           "px-4 py-3 rounded-xl text-sm font-bold border transition-all",
                           formData.os === os 
                             ? "bg-purple-600 border-purple-600 text-white shadow-lg shadow-purple-500/20" 
                             : isDarkMode ? "bg-zinc-800 border-zinc-700 hover:bg-zinc-700" : "bg-zinc-50 border-zinc-200 hover:bg-zinc-100"
                        )}
                      >
                        {os}
                      </button>
                   ))}
                </div>
              </div>	

              <button 
                type="submit" 
                disabled={status === 'loading'}
                className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold shadow-xl shadow-purple-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? <Loader2 className="animate-spin" /> : "Request Early Access"}
              </button>
              
              <p className="text-[10px] text-center opacity-40">
                Source detected: <span className="font-mono">{analyticsSource}</span>
              </p>
           </form>
        </div>
      </div>
    </section>
  );
};


export default function LandingPage() {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [roleIndex, setRoleIndex] = useState(0);
  const currentRoleData = ROLE_DATA[roleIndex];
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setRoleIndex((prev) => (prev + 1) % ROLE_DATA.length);
    }, 4300);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={cn(
      "min-h-screen font-sans transition-colors duration-500",
      isDarkMode ? "bg-zinc-950 text-zinc-50" : "bg-zinc-50 text-zinc-900"
    )}>

      {/* --- NAV --- */}
      <nav className={cn(
        "fixed top-0 w-full z-50 backdrop-blur-md border-b flex items-center h-16 transition-colors",
        isDarkMode ? "bg-zinc-950/80 border-zinc-800" : "bg-white/80 border-zinc-200"
      )}>
        <div className="flex justify-between items-center px-6 max-w-7xl mx-auto w-full">
          <div className="flex items-center gap-2 font-black text-xl tracking-tighter italic">
	  {/* <Zap className="text-purple-500 fill-current" size={22} />
            THRUST
	    */}

 <img src="./thrust_tracing_white.png"
	  height="100px" width="100px"/>

	  THRUST
       
	  </div>
          <div className="flex items-center gap-6">
            <div className="hidden md:flex items-center gap-8 text-sm font-bold tracking-tight">
                <a href="#features" className="hover:text-purple-500 transition-colors">Features</a>
                <a href="#pricing" className="hover:text-purple-500 transition-colors">Pricing</a>
                <a href="#early-access" className="hover:text-purple-500 transition-colors">Early Access</a>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => setIsDarkMode(!isDarkMode)} className={cn(
                "p-2 rounded-lg transition-colors",
                isDarkMode ? "hover:bg-zinc-800 text-zinc-400" : "hover:bg-zinc-100 text-zinc-500"
              )}>
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button
                onClick={() => document.getElementById('early-access')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-purple-600 text-white px-5 py-2 rounded-lg text-sm font-bold shadow-lg shadow-purple-500/20 hover:bg-purple-700 transition-all">
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <section className="px-6 pt-32 pb-24 text-center max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-8xl font-black tracking-tight mb-6 leading-[0.9]"> All {currentRoleData.article} <span className="text-purple-400 relative inline-block text-left">
          <AnimatePresence mode="wait">
             <motion.span
               key={currentRoleData.label}
               initial={{ y: 20, opacity: 0 }}
               animate={{ y: 0, opacity: 1 }}
               transition={{ duration: 0.4 }}
               className="block absolute top-0 left-0 whitespace-nowrap"
             >
               {currentRoleData.label}
             </motion.span>
          </AnimatePresence>
          <span className="invisible">{currentRoleData.label}</span>
          </span>
          <br />needs is focus. </h1>

          <p className={cn("text-lg md:text-xl mb-10 max-w-2xl mx-auto font-medium", isDarkMode ? "text-zinc-400" : "text-zinc-500")}> The proactive guide that monitors your workflow and eliminates development fatigue before it starts, so you can always focus on what matters.</p> 
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4"> 
            <button
                onClick={() => document.getElementById('early-access')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full sm:w-auto px-10 py-4 bg-purple-600 rounded-xl font-bold text-white hover:bg-purple-500 transition-all"> 
                Request Beta Access 
            </button> 
	  {/*  <button className="w-full sm:w-auto px-10 py-4 border border-zinc-500/20 rounded-xl font-bold hover:bg-zinc-500/5 transition-all"> Watch Demo </button> */}
          </div> 
      </section>

      {/* --- DASHBOARD UI --- */}
      <section className="px-6 pb-24 max-w-6xl mx-auto">
        <div className={cn(
          "rounded-3xl p-2 border transition-all duration-500 shadow-2xl",
          isDarkMode ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200"
        )}>
          <div className={cn(
            "rounded-2xl overflow-hidden border min-h-[500px]",
            isDarkMode ? "bg-zinc-950 border-zinc-800" : "bg-zinc-50 border-zinc-100"
          )}>
             {/* Header */}
             <div className={cn(
               "h-10 border-b flex items-center px-4 justify-between",
               isDarkMode ? "bg-zinc-900/50 border-zinc-800" : "bg-white border-zinc-100"
             )}>
                <div className="flex gap-1.5">
                   <div className="w-2.5 h-2.5 rounded-full bg-red-500/40" />
                   <div className="w-2.5 h-2.5 rounded-full bg-amber-500/40" />
                   <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/40" />
                </div>
                <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">morning_briefing.thrust</div>
             </div>

             {/* Content */}
             <div className="p-8 md:p-12 grid lg:grid-cols-[1.5fr_1fr] gap-12">
                <div className="space-y-8">
                  <motion.div key={currentRoleData.project}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.2 }} 
                  >
                     <div className="flex items-center gap-3 mb-4">
                        <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-500 text-[10px] font-bold uppercase border border-purple-500/20">Active Context</span>
                        <span className="text-xs text-zinc-500 font-mono">09:00 AM</span>
                     </div>
                     <h3 className="text-3xl font-black">{currentRoleData.project}</h3>
                   </motion.div>

                   <div className={cn(
                     "p-6 rounded-2xl border-l-4 border-purple-500 shadow-sm transition-colors",
                     isDarkMode ? "bg-zinc-900 text-zinc-300" : "bg-white text-zinc-700"
                   )}>
                      <p className="font-mono text-sm mb-6 opacity-80">{currentRoleData.primaryThrust}</p>
                      <ul className="space-y-4 text-sm font-bold">
                        {currentRoleData.tasks.map((task, i) => (
                          <li key={i} className="flex gap-4">
                             <span className="text-purple-500 font-mono">0{i+1}</span>
                             {task}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-8 p-4 bg-red-500/5 border border-red-500/10 rounded-xl flex gap-3 items-start">
                         <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                         <p className="text-xs font-bold text-red-500 leading-relaxed">{currentRoleData.risk}</p>
                      </div>
                   </div>
                </div>

                <div className="space-y-8 lg:border-l lg:pl-12 transition-colors border-zinc-800">
                   <div>
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-6">Inputs Analyzed</h4>
                      <div className="space-y-4">
                         {currentRoleData.inputs.map((input, i) => (
                           <div key={i} className={cn("flex items-center gap-4 p-3 rounded-xl border", isDarkMode ? "bg-zinc-900/50 border-zinc-800" : "bg-white border-zinc-100 shadow-sm")}>
                              <div className="p-2 bg-zinc-800 rounded-lg text-white"><input.icon size={16} /></div>
                              <span className={cn("text-xs font-bold", input.color.split(' ')[0])}>{input.label}</span>
                           </div>
                         ))}
                      </div>
                   </div>
                   <div>
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-4">Context Health</h4>
                      <div className="h-2 w-full bg-zinc-800 rounded-full overflow-hidden">
                         <div className="h-full bg-emerald-500 w-[92%] shadow-[0_0_15px_rgba(16,185,129,0.3)]" />
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* --- PROBLEM SECTION --- */}
      <section className={cn(
        "py-24 px-6 border-y text-center transition-colors",
        isDarkMode ? "bg-zinc-900/20 border-zinc-900" : "bg-zinc-100 border-zinc-200"
      )}>
        <h2 className="text-sm font-black text-purple-500 uppercase tracking-widest mb-6">The Brutal Truth</h2>
        <h3 className="text-4xl md:text-6xl font-black mb-8 max-w-4xl mx-auto leading-[1.1]">
          90% of projects fail because of <span className="italic underline decoration-purple-500">Context Decay.</span>
        </h3>
        <p className={cn("text-xl max-w-2xl mx-auto font-medium leading-relaxed", isDarkMode ? "text-zinc-400" : "text-zinc-600")}>
          It's not a skill issue. It's the mental cost of remembering where you left off and the next thing to do.
          When the "why" fades, fatigue sets in, and the project dies.
          <strong> Thrust keeps the context alive while you focus on what matters.</strong>
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto mt-20 text-left">
           <GraveyardCard isDarkMode={isDarkMode} icon={Code2} title="The VS Code Trap" quote="Where was I? Oh right, fixing that bug..." sub="(Spends 2 hours re-reading code)" />
           <GraveyardCard isDarkMode={isDarkMode} icon={Layout} title="The Figma Disconnect" quote="Wait, did the design change again?" sub="(Implements wrong border-radius)" />
           <GraveyardCard isDarkMode={isDarkMode} icon={Terminal} title="The Deployment Limbo" quote="I'll configure AWS later..." sub="(Project never leaves localhost)" />
        </div>
      </section>

      {/* --- METHODOLOGY --- */}
      <section id="features" className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-6xl font-black">Your GPS for Flow State</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
           <FeatureCard isDarkMode={isDarkMode} icon={BrainCircuit} title="Proactive Thrusts" desc="Morning briefings that tell you exactly what has been done and what to do next before you even open your laptop and Flow Thrusts keeps you in check as you work." />
           <FeatureCard isDarkMode={isDarkMode} icon={GitBranch} title="Cross-Ecosystem Sync" desc="We build a chronological truth by connecting VS Code edits, GitHub PRs and Figma component drifts natively. More tool support is being added regularly." />
           <FeatureCard isDarkMode={isDarkMode} icon={Cpu} title="Fast and Safe Delta Digestion" desc="We only analyze files within your project scope. Fast, private, and ZERO training on your proprietary data." />
        </div>
      </section>

      {/* --- AGENT AWARENESS (MCP SECTION) --- */}
      <section id="mcp" className={cn(
          "py-24 px-6 border-b overflow-hidden",
          isDarkMode ? "bg-zinc-900/10" : "bg-white"
      )}>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
                <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-[0.9]">Agent <br/> Awareness.</h2>
                <p className={cn("text-lg font-medium leading-relaxed", isDarkMode ? "text-zinc-400" : "text-zinc-600")}>
                    Every AI model you use is now a project expert on your specific project.
                    Through our MCP (Model Context Protocol) server, THRUST serves your current project state and goals directly into your AI agentic workflows.
                    <br/><br/>
                    Whether it's Claude Code, OpenAI, or n8n, your models remain in perfect sync with your ultimate project aim giving you the most flexibility.
                </p>
                <div className="flex flex-wrap gap-4">
                    {['n8n', 'Claude Code', 'GPT-5', 'Cursor', 'Gemini'].map((bot) => (
                        <div key={bot} className={cn("px-4 py-2 rounded-lg border font-bold text-sm", isDarkMode ? "bg-zinc-800 border-zinc-700" : "bg-zinc-50 border-zinc-200")}>
                            {bot}
                        </div>
                    ))}
                </div>
            </div>

            <div className={cn(
                "rounded-2xl border p-1 shadow-2xl relative group",
                isDarkMode ? "bg-zinc-800 border-zinc-700" : "bg-zinc-200 border-zinc-300"
            )}>
                <div className={cn("rounded-xl p-8 font-mono text-sm space-y-6", isDarkMode ? "bg-zinc-950" : "bg-[#1E1E1E] text-zinc-300")}>
                    <div className="flex items-center gap-3 text-emerald-500 font-bold border-b border-white/5 pb-4">
                        <CpuIcon size={18} /> MCP Server: Active [Port 3000]
                    </div>
                    <div className="space-y-2 opacity-80">
                        <p className="text-zinc-500">&gt; Serving project_requirements.md</p>
                        <p className="text-zinc-500">&gt; Syncing local file tree hashes...</p>
                        <p className="text-zinc-500">&gt; Awaiting agent handshake...</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-lg border border-white/5 space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-purple-400">Agent: Claude Code</span>
                            <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-500 rounded-full">Sync Complete</span>
                        </div>
                        <p className="text-xs text-zinc-400 italic">"I see your aim is the E-Commerce Refactor. Your requirements say the Stripe logic is priority. I've analyzed the delta in your last commit..."</p>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-zinc-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        AWARENESS BROADCASTING IN REAL-TIME
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* --- PRICING --- */}
      <section id="pricing" className={cn(
        "py-24 px-6 border-y transition-colors",
        isDarkMode ? "bg-zinc-900/30 border-zinc-900" : "bg-white border-zinc-200"
      )}>
        <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-black text-center mb-20">Focused Pricing.</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className={cn("p-8 rounded-3xl border flex flex-col h-full", isDarkMode ? "bg-zinc-950 border-zinc-800" : "bg-zinc-50 border-zinc-200")}>
                    <h4 className="text-xl font-bold mb-2">Observer</h4>
                    <p className="text-4xl font-black mb-8">$0</p>
                    <ul className="space-y-4 mb-10 flex-1 text-sm font-bold opacity-70">
                        <li className="flex gap-3"><CheckCircle2 size={18} /> Haiku-speed Briefings</li>
                        <li className="flex gap-3"><CheckCircle2 size={18} /> 1 Active Project</li>
                        <li className="flex gap-3"><CheckCircle2 size={18} /> 2 Data Sources</li>
                    </ul>
                    <button onClick={() => document.getElementById('early-access')?.scrollIntoView({ behavior: 'smooth' })} className={cn("w-full py-4 rounded-xl font-bold border transition-all", isDarkMode ? "border-zinc-800 hover:bg-zinc-800" : "bg-white border-zinc-200 hover:bg-zinc-50")}>Start Free</button>
                </div>

                <div className={cn("p-8 rounded-3xl border-2 border-purple-600 relative flex flex-col h-full scale-105 shadow-2xl z-10", isDarkMode ? "bg-zinc-900" : "bg-white")}>
                    <div className="absolute top-0 right-0 bg-purple-600 text-white text-[10px] font-black px-4 py-1 rounded-bl-xl uppercase">Most Popular</div>
                    <h4 className="text-xl font-bold mb-2">Flow State</h4>
                    <p className="text-4xl font-black mb-8">$10 <span className="text-sm opacity-50">/mo</span></p>
                    <ul className="space-y-4 mb-10 flex-1 text-sm font-bold">
                        <li className="flex gap-3"><CheckCircle2 className="text-purple-500" size={18} /> Sonnet 4.6 Deep Briefings</li>
                        <li className="flex gap-3"><CheckCircle2 className="text-purple-500" size={18} /> 10-Min Context Refresh</li>
                        <li className="flex gap-3"><CheckCircle2 className="text-purple-500" size={18} /> Unlimited Integrations</li>
                    </ul>
                    <button onClick={() => document.getElementById('early-access')?.scrollIntoView({ behavior: 'smooth' })} className="w-full py-4 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 shadow-xl shadow-purple-500/20">Upgrade to Pro</button>
                </div>

                <div className={cn("p-8 rounded-3xl border flex flex-col h-full", isDarkMode ? "bg-zinc-950 border-zinc-800" : "bg-zinc-50 border-zinc-200")}>
                    <h4 className="text-xl font-bold mb-2">Architect</h4>
                    <p className="text-4xl font-black mb-8">$20 <span className="text-sm opacity-50">/mo</span></p>
                    <ul className="space-y-4 mb-10 flex-1 text-sm font-bold opacity-70">
                        <li className="flex gap-3"><CheckCircle2 size={18} /> Instant Real-time Thrusts</li>
                        <li className="flex gap-3"><CheckCircle2 size={18} /> Multi-Context Sync</li>
                        <li className="flex gap-3"><CheckCircle2 size={18} /> Priority Requirements Sync</li>
                    </ul>
                    <button onClick={() => document.getElementById('early-access')?.scrollIntoView({ behavior: 'smooth' })} className={cn("w-full py-4 rounded-xl font-bold border transition-all", isDarkMode ? "border-zinc-800 hover:bg-zinc-800" : "bg-white border-zinc-200 hover:bg-zinc-50")}>Upgrade to Pro</button>
                </div>
            </div>
        </div>
      </section>

      {/* --- EARLY ACCESS --- */}
      <EarlyAccess isDarkMode={isDarkMode} />

      {/* --- FOOTER --- */}
      <footer className={cn(
          "pt-24 pb-12 px-6 transition-colors",
          isDarkMode ? "bg-black" : "bg-zinc-100"
      )}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 border-b border-zinc-800 pb-20">
            <div className="space-y-6">
                <div className="flex items-center gap-2 font-black text-2xl tracking-tighter italic">
                    <Zap className="text-purple-500 fill-current" size={24} />
                    THRUST
                </div>
                <p className={cn("text-sm font-medium leading-relaxed", isDarkMode ? "text-zinc-500" : "text-zinc-500")}>
                    Eliminating context decay for the modern professional. Built for high-velocity builders.
                </p>
            </div>

            <div>
                <h5 className="font-bold mb-6 text-sm uppercase tracking-widest text-purple-500">Product</h5>
                <ul className="space-y-4 text-sm font-bold opacity-70">
                    <li><a href="#features" className="hover:text-purple-500 transition-colors">Features</a></li>
                    <li><a href="#pricing" className="hover:text-purple-500 transition-colors">Pricing</a></li>
                    <li><a href="#mcp" className="hover:text-purple-500 transition-colors">MCP Server</a></li>
                    <li><a href="#" className="hover:text-purple-500 transition-colors">Desktop App</a></li>
                </ul>
            </div>

            <div>
                <h5 className="font-bold mb-6 text-sm uppercase tracking-widest text-purple-500">Company</h5>
                <ul className="space-y-4 text-sm font-bold opacity-70">
                    <li><a href="mailto:publicstring.care@gmail.com" className="hover:text-purple-500 transition-colors">Feedback</a></li>
                    <li><a href="mailto:publicstring.care@gmail.com" className="hover:text-purple-500 transition-colors">Support</a></li>
                </ul>
            </div>

            <div>
                <h5 className="font-bold mb-6 text-sm uppercase tracking-widest text-purple-500">Legal</h5>
                <ul className="space-y-4 text-sm font-bold opacity-70">
                    <li><a href="/terms" className="hover:text-purple-500 transition-colors">Terms of Service</a></li>
                    <li><a href="/privacy" className="hover:text-purple-500 transition-colors">Privacy Policy</a></li>
                </ul>
            </div>
        </div>
        <div className="max-w-7xl mx-auto flex flex-col md:row items-center justify-between pt-12 gap-6">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-30">
                Stop Guessing. Start Thrusting. © 2026
            </p>
            <div className="flex items-center gap-6">
                <ShieldCheck size={18} className="opacity-30" />
                <Globe size={18} className="opacity-30" />
                <Mail size={18} className="opacity-30" />
            </div>
        </div>
      </footer>
    </div>
  );
}
